import { LitElement, PropertyValueMap, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export interface ToastOptions {
  title?: string;
  message?: string;
  icon?: string;
  duration?: number;
  cookieName?: string;
  cookieValue?: string;
  removeCookieOnShow?: boolean;
  showProgressBar?: boolean;
  showCloseButton?: boolean;
  position?: 'top' | 'bottom';
  offsetTop?: number;
  type?: 'success' | 'warning' | 'error' | 'info';
  closeMode?: 'auto' | 'manual' | 'auto-and-manual';
}

@customElement('tm-toast')
export class TmToast extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String }) title: string = 'Toda Matéria+ ativado!';
  @property({ type: String }) message: string = 'Acesso total liberado por 7 dias.';
  @property({ type: String }) icon: string = '';
  @property({ type: Number }) duration: number = 5000;
  @property({ type: String }) cookieName: string = '';
  @property({ type: String }) cookieValue: string = '';
  @property({ type: Boolean }) removeCookieOnShow: boolean = false;
  @property({ type: Boolean }) showProgressBar: boolean = true;
  @property({ type: Boolean }) showCloseButton: boolean = true;
  @property({ type: String }) position: 'top' | 'bottom' = 'top';
  @property({ type: Number }) offsetTop: number = 10;
  @property({ type: String }) type: 'success' | 'warning' | 'error' | 'info' = 'success';
  @property({ type: String }) closeMode: 'auto' | 'manual' | 'auto-and-manual' = 'auto-and-manual';
  @property({ type: String }) width: string = '310px';
  @property({ type: String }) customCssClass: string = '';
  @property({ type: String }) iconPosition: 'block' | 'inline' = 'inline';


  @state() private isVisible: boolean = false;
  @state() private isRemoving: boolean = false;
  @state() private progressWidth: number = 0;
  @state() private finalMessage: string = '';
  @state() private topPosition: number = 0;
  @state() private stackIndex: number = 0;

  private spacingBetweenToasts: number = 8;

  private timeoutId: NodeJS.Timeout | null = null;
  private progressInterval: NodeJS.Timeout | null = null;

  private eventsListenersToCalculatePosition = [
    'toast-removed',
    'resize',
    'showheader',
    'hideheader',
    'scroll'
  ]

  connectedCallback(): void {
    super.connectedCallback();
    this.initializeToast();
    
    // Listen for toast removal events to recalculate positions
    this.eventsListenersToCalculatePosition.forEach(event => {
      document.addEventListener(event, this.handleToastRemoved.bind(this) as EventListener);
    });
  }

  disconnectedCallback(): void {
    this.clearTimeouts();
    // Remove event listener
    this.eventsListenersToCalculatePosition.forEach(event => {
      document.removeEventListener(event, this.handleToastRemoved.bind(this) as EventListener);
    });
    super.disconnectedCallback();
  }

  private handleToastRemoved(event: Event): void {
    const customEvent = event as CustomEvent;
    // Only recalculate if this toast is not the one being removed
    if (customEvent?.detail?.removedToast !== this) {
      // Update stack index and recalculate position
      this.updateStackIndex();
    }
    this.calculatePosition(false);
    
  }

  private updateStackIndex(): void {
    // Get all toasts and find this toast's new position
    const allToasts = document.querySelectorAll('tm-toast');
    const toastArray = Array.from(allToasts);
    const currentIndex = toastArray.indexOf(this);
    
    // Update stack index to current position
    this.stackIndex = currentIndex;
  }

  updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    
    if (changedProperties.has('message') || changedProperties.has('cookieName')) {
      this.updateMessage();
    }
  }

  private getCookieValue(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const result = parts.pop()?.split(';').shift();
      return result || null;
    }
    return null;
  }

  private removeCookie(cookieName: string): void {
    document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }

  private getDefaultIcon(): string {
    if (this.icon) return this.icon;
    
    switch (this.type) {
      case 'success':
        return 'enem-icon-complete-green-check';
      case 'warning':
        return 'enem-icon-warning';
      case 'error':
        return 'enem-icon-error';
      case 'info':
        return 'enem-icon-info';
      default:
        return 'enem-icon-complete-green-check';
    }
  }

  private updateMessage(): void {
    if (this.cookieName && !this.cookieValue) {
      const cookieValue = this.getCookieValue(this.cookieName);
      if (cookieValue) {
        this.finalMessage = `Acesso total liberado por ${cookieValue} dias.`;
      } else {
        this.finalMessage = this.message;
      }
    } else if (this.cookieValue) {
      this.finalMessage = `Acesso total liberado por ${this.cookieValue} dias.`;
    } else {
      this.finalMessage = this.message;
    }
  }

  private initializeToast(): void {
    this.updateMessage();
    
    // Position the toast
    this.calculatePosition();
    
    // Initialize progress bar at 0
    this.progressWidth = 0;
    
    // Show toast with animation
    requestAnimationFrame(() => {
      this.isVisible = true;
      // Only start progress bar if auto-removal is enabled
      if (this.closeMode === 'auto' || this.closeMode === 'auto-and-manual') {
        this.startProgressBar();
      }
    });

    // Set up auto-removal based on close mode
    if (this.duration > 0 && (this.closeMode === 'auto' || this.closeMode === 'auto-and-manual')) {
      this.timeoutId = setTimeout(() => {
        this.removeToast();
      }, this.duration);
    }

    // Remove cookie if specified
    if (this.cookieName && this.removeCookieOnShow) {
      this.removeCookie(this.cookieName);
    }
  }

  private calculatePosition(updateStackIndex: boolean = true): void {
    if (this.position === 'top') {
      const mainHeader = document.querySelector("#main-header");
      let baseTop = 8; // Default top position
      
      if (mainHeader) {
        const hasHiddenClass = (mainHeader as HTMLElement).classList.contains('header--hidden');
        const mainHeaderHeight = (mainHeader as HTMLElement).offsetHeight;
        if (mainHeaderHeight && !hasHiddenClass) {
          baseTop = mainHeaderHeight + this.offsetTop;
        }
      }

      let toastsTotalHeight = 0;
      
      if (updateStackIndex) {
        // Initial positioning: calculate based on existing toasts
        const allToasts = document.querySelectorAll('tm-toast');
        const existingToasts = Array.from(allToasts).filter(toast => toast !== this);
        
        existingToasts.forEach((toast) => {
          const toastHeight = ((toast as HTMLElement).querySelector('.tm-toast') as HTMLElement)?.offsetHeight || 0;
          toastsTotalHeight += toastHeight;
        });
        
        // Assign this toast's position in the stack
        this.stackIndex = existingToasts.length;
      } else {
        // Repositioning: calculate based on toasts before this one
        const allToasts = document.querySelectorAll('tm-toast');
        const toastArray = Array.from(allToasts);
        
        for (let i = 0; i < this.stackIndex; i++) {
          const toast = toastArray[i];
          if (toast) {
            const toastHeight = ((toast as HTMLElement).querySelector('.tm-toast') as HTMLElement)?.offsetHeight || 0;
            toastsTotalHeight += toastHeight;
          }
        }
      }
      
      // Calculate position based on total height and spacing
      this.topPosition = baseTop + toastsTotalHeight + (this.spacingBetweenToasts * this.stackIndex);
    } else {
      this.topPosition = this.offsetTop;
    }
  }

  private startProgressBar(): void {
    if (!this.showProgressBar || this.duration <= 0) return;

    const startTime = Date.now();
    const endTime = startTime + this.duration;

    this.progressInterval = setInterval(() => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min((elapsed / this.duration) * 100, 100);
      
      this.progressWidth = progress;

      if (progress >= 100 || currentTime >= endTime) {
        this.progressWidth = 100;
        this.clearProgressInterval();
      }
    }, 16);
  }

  private clearProgressInterval(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private clearTimeouts(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.clearProgressInterval();
  }

  private handleCloseClick(): void {
    this.clearTimeouts();
    this.removeToast();
  }

  private removeToast(): void {
    this.isVisible = false;
    this.isRemoving = true;
    this.clearTimeouts();

    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      if (this.parentNode) {
        this.parentNode.removeChild(this);
        // Dispatch custom event to notify other toasts to recalculate positions
        document.dispatchEvent(new CustomEvent('toast-removed', { detail: { removedToast: this } }));
      }
    }, 300); // Match the CSS transition duration
  }

  // Public API methods
  public show(): void {
    this.isVisible = true;
    this.isRemoving = false;
  }

  public hide(): void {
    this.removeToast();
  }

  public clearTimeout(): void {
    this.clearTimeouts();
  }

  render() {
    let classes = {
      'tm-toast': true,
      'tm-toast--visible': this.isVisible,
      'tm-toast--removing': this.isRemoving,
      [`tm-toast--${this.type}`]: true,
      'tm-toast--icon-position-inline': this.iconPosition == 'inline',
      'tm-toast--icon-position-block': this.iconPosition == 'block'
    };
    if(this.customCssClass) {
        const customClasses = this.customCssClass ? this.customCssClass.split(' ') : [];
        classes = {
          ...classes,
          ...customClasses.reduce((acc, className) => {
            acc[className] = true;
            return acc;
          }, {} as Record<string, boolean>)
        };
    }

    if(this.iconPosition == 'inline') {
        return html`
        <div class="${classMap(classes)}" style="top: ${this.topPosition}px; width: ${this.width};">
            <div class="tm-toast__header">
                <span class="${this.getDefaultIcon()}"></span>
                <span class="tm-toast__header-text">${unsafeHTML(this.title)}</span>
            </div>
            <div class="tm-toast__text">${unsafeHTML(this.finalMessage)}</div>
            ${this.showProgressBar ? html`
            <div class="tm-toast__progress-bar-container">
                <div class="tm-toast__progress-bar" style="width: ${this.progressWidth}%;"></div>
            </div>
            ` : nothing}
            ${this.showCloseButton && (this.closeMode === 'manual' || this.closeMode === 'auto-and-manual') ? html`
            <div class="tm-toast__close-button-container" @click="${this.handleCloseClick}">
                <div class="enem-icon-close"></div>
            </div>
            ` : nothing}
        </div>
        `;
    }
    
    if(this.iconPosition == 'block') {
        return html`
        <div class="${classMap(classes)}" style="top: ${this.topPosition}px; width: ${this.width};">
            <div class="${this.getDefaultIcon()}"></div>
            <div class="tm-toast__content">
                <div class="tm-toast__header">
                    <span class="tm-toast__header-text">${unsafeHTML(this.title)}</span>
                </div>
                <div class="tm-toast__text">${unsafeHTML(this.finalMessage)}</div>
                ${this.showProgressBar ? html`
                <div class="tm-toast__progress-bar-container">
                    <div class="tm-toast__progress-bar" style="width: ${this.progressWidth}%;"></div>
                </div>
                ` : nothing}
            </div>
            ${this.showCloseButton && (this.closeMode === 'manual' || this.closeMode === 'auto-and-manual') ? html`
            <div class="tm-toast__close-button-container" @click="${this.handleCloseClick}">
                <div class="enem-icon-close"></div>
            </div>
            ` : nothing}
        </div>
        `;
    }
  }
}

// Utility function to create and show a toast programmatically
export const showToast = (options: ToastOptions = {}): TmToast => {
  const toast = document.createElement('tm-toast') as TmToast;
  
  // Set properties from options
  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined) {
      (toast as any)[key] = value;
    }
  });

  document.body.appendChild(toast);
  return toast;
};

declare global {
  interface HTMLElementTagNameMap {
    'tm-toast': TmToast;
  }
} 